export enum AppointmentStatus {
  /**
   * Locked is now derived property when status is set (on the mongo document)
   */
  // locked = 0,

  /// A official has been rostered, but can't view the appointment yet
  rostered = 1,
  /// A official has been rostered, and the appointment has been published.
  published = 2,

  /// A official has confirmed the appointment (see rejected = 22)
  confirmed = 3,
  /// A official has completed the appointment, and marked that they completed this appointment
  completed = 4,

  /// Marked as payment processing
  paymentProcessing = 10,
  /// Payment has been processed
  paymentProcessed = 11,
  /// Payment was processed in person, maybe by signing a timesheet
  paymentProcessedInPerson = 12,

  /// The official was removed from the appointment
  removed = 21,
  /// The official rejected the appointment.
  rejected = 22,
  /// The official rejected the appointment, and someone acknowledged this.
  rejectedAcknowledged = 23,
  /// The official said that although they were rostered, they did not complete this appointment. Someone needs to correct this appointment
  notCompleted = 24,
  /// The official said they they didn't complete this appointment and someone acknowledged this
  notCompletedAcknowledged = 25,
}

export namespace AppointmentStatus {
  export function showOnDashboard(
    appointmentStatus: AppointmentStatus,
  ): boolean {
    switch (appointmentStatus) {
      case AppointmentStatus.published:
      case AppointmentStatus.confirmed:
        return true;
      default:
        return false;
    }
  }

  export function isLocked(appointmentStatus: AppointmentStatus): boolean {
    switch (appointmentStatus) {
      case AppointmentStatus.rejectedAcknowledged:
      case AppointmentStatus.completed:
      case AppointmentStatus.paymentProcessed:
      case AppointmentStatus.paymentProcessedInPerson:
        return true;
      default:
        return false;
    }
  }

  export function stillAppointed(
    appointmentStatus: AppointmentStatus,
  ): boolean {
    switch (appointmentStatus) {
      case AppointmentStatus.removed:
      case AppointmentStatus.rejectedAcknowledged:
      case AppointmentStatus.notCompletedAcknowledged:
        return false;
      default:
        return true;
    }
  }

  export function possibleTransitions(
    appointmentStatus: AppointmentStatus,
  ): AppointmentStatus[] {
    switch (appointmentStatus) {
      case AppointmentStatus.rostered:
        return [AppointmentStatus.removed, AppointmentStatus.published];
      case AppointmentStatus.published:
        return [AppointmentStatus.removed, AppointmentStatus.rejected];
      case AppointmentStatus.confirmed:
        return [
          AppointmentStatus.rejected,
          AppointmentStatus.completed,
          AppointmentStatus.notCompleted,
        ];
      case AppointmentStatus.removed:
        return [AppointmentStatus.rejectedAcknowledged];
      case AppointmentStatus.completed:
        return [AppointmentStatus.paymentProcessing];
      case AppointmentStatus.rejected:
        return [AppointmentStatus.rejectedAcknowledged];
      case AppointmentStatus.notCompleted:
        return [AppointmentStatus.notCompletedAcknowledged];
      case AppointmentStatus.paymentProcessing:
        return [AppointmentStatus.paymentProcessed];

      case AppointmentStatus.rejectedAcknowledged:
      case AppointmentStatus.notCompletedAcknowledged:
      case AppointmentStatus.paymentProcessed:
      default:
        return [];
    }
  }

  export function toString(appointmentStatus: AppointmentStatus): string {
    switch (appointmentStatus) {
      case AppointmentStatus.rostered:
        return "Rostered";
      case AppointmentStatus.published:
        return "Published";
      case AppointmentStatus.rejected:
        return "Rejected";
      case AppointmentStatus.rejectedAcknowledged:
        return "Rejected (acknowledged)";
      case AppointmentStatus.confirmed:
        return "Confirmed";
      case AppointmentStatus.completed:
        return "Completed";
      case AppointmentStatus.removed:
        return "Removed";
      case AppointmentStatus.notCompleted:
        return "Not completed";
      case AppointmentStatus.notCompletedAcknowledged:
        return "Not completed (acknowledged)";
      case AppointmentStatus.paymentProcessedInPerson:
        return "Processed in-person";

      case AppointmentStatus.paymentProcessing:
        "Processing payment";
      case AppointmentStatus.paymentProcessed:
        "Payment processed";
    }
    return "";
  }

  export function canRemoveOrChange(status: AppointmentStatus): boolean {
    switch (status) {
      case AppointmentStatus.rostered:
      case AppointmentStatus.published:
      case AppointmentStatus.confirmed:
      case AppointmentStatus.notCompleted:
      case AppointmentStatus.rejected:
        return true;
      default:
        return false;
    }
  }

  export function requiresAction(
    appointmentStatus: AppointmentStatus,
  ): boolean {
    switch (appointmentStatus) {
      case AppointmentStatus.rejected:
      case AppointmentStatus.notCompleted:
        return true;
    }
    return false;
  }

  export function shouldShowTick(
    appointmentStatus: AppointmentStatus,
  ): boolean {
    switch (appointmentStatus) {
      case AppointmentStatus.confirmed:
      case AppointmentStatus.completed:
      case AppointmentStatus.paymentProcessing:
      case AppointmentStatus.paymentProcessed:
        return true;
      default:
        return false;
    }
  }

  export function toColor(appointmentStatus: AppointmentStatus): string {
    switch (appointmentStatus) {
      case AppointmentStatus.rejected:
      case AppointmentStatus.rejectedAcknowledged:
      case AppointmentStatus.notCompleted:
      case AppointmentStatus.notCompletedAcknowledged:
      case AppointmentStatus.removed:
        // red
        return "#EBAFAF";

      case AppointmentStatus.completed:
      case AppointmentStatus.paymentProcessed:
      case AppointmentStatus.paymentProcessedInPerson:
        // green
        return "#A8D199";
      case AppointmentStatus.published:
      case AppointmentStatus.confirmed:
        // yellow
        return "#F8EBB7";
      case AppointmentStatus.rostered:
        // grey
        return "#E8E8E8";
    }
    return "#E8E8E8";
  }
}
